import { useState, type ComponentType, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiErrorException } from '@/api/client';
import { ensureSipDomain, importZadarmaNumber, type SipDomainResult, type ZadarmaImportResult } from '@/api/endpoints/carriers';
import { useOrganisations } from '@/hooks/queries/useAdmin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ErrorLine } from '@/components/backoffice/platform/PlatformBits';

// components/ui/select is typed without `id`; the trigger takes it (label association).
const SelectTriggerWithId = SelectTrigger as unknown as ComponentType<{ id: string; className?: string; children?: ReactNode }>;

/**
 * SU-4 (W7): « SIP et Zadarma » in the « Opérateurs » tab of TwilioConfig.
 * Two routes that had no caller in the front end:
 *   - POST /telephony/admin/sip-domain: creates or updates the Twilio SIP
 *     domain that receives the calls of the Zadarma numbers (idempotent, so
 *     no confirmation: running it twice changes nothing);
 *   - POST /telephony/admin/zadarma-import: imports a number bought by hand on
 *     the Zadarma panel for an organisation, points its SIP routing at the
 *     domain and starts the caller ID check (idempotent too: a second import
 *     updates the number).
 * The server guard (req.auth.isSuperAdmin) is unchanged. Each server code
 * gets its own translated message, with the code under it (DES-16).
 */

const E164 = /^\+[1-9]\d{6,14}$/;
const NUMBER_STATUSES = ['active', 'suspended', 'released', 'provisioning'];
const CALLER_ID_STATES = ['', 'pending', 'verified', 'failed'];
const KNOWN_CODES = ['NO_DOMAIN', 'NO_TENANT', 'NO_PHONE', 'NUMBER_NOT_ON_ZADARMA', 'ZADARMA_NOT_CONFIGURED', 'TWILIO_NOT_CONFIGURED', 'NO_SIP_DOMAIN', 'FORBIDDEN'];

function useCodeMessage() {
  const { t } = useTranslation(['admin']);
  return (err: unknown, fallback: string) => {
    const code = err instanceof ApiErrorException ? err.code : '';
    return code && KNOWN_CODES.includes(code) ? t(`admin:twilioConfig.sipZadarma.errors.${code}`) : fallback;
  };
}

function ResultList({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-1 gap-x-3 gap-y-1 text-sm sm:grid-cols-[12rem_1fr]" aria-live="polite">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--vf-text-3)' }}>{k}</dt>
          <dd className="min-w-0 break-all font-mono text-xs">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function SipDomainForm() {
  const { t } = useTranslation(['admin']);
  const codeMessage = useCodeMessage();
  const [domain, setDomain] = useState('');
  const run = useMutation<SipDomainResult, unknown, string>({
    mutationFn: (name) => ensureSipDomain(name),
    onSuccess: (r) => toast.success(r.created ? t('admin:twilioConfig.sipZadarma.sip.created') : t('admin:twilioConfig.sipZadarma.sip.updated')),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    run.mutate(domain);
  };
  const r = run.data;
  return (
    <form onSubmit={submit} className="space-y-3" aria-labelledby="sip-domain-title" noValidate>
      <h3 id="sip-domain-title" className="text-sm font-semibold">{t('admin:twilioConfig.sipZadarma.sip.title')}</h3>
      <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.sipZadarma.sip.desc')}</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[16rem] flex-1 space-y-1">
          <Label htmlFor="sip-domain-name">{t('admin:twilioConfig.sipZadarma.sip.domain')}</Label>
          <Input id="sip-domain-name" value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="xdial.sip.twilio.com" autoComplete="off" aria-describedby="sip-domain-hint" />
          <p id="sip-domain-hint" className="text-xs" style={{ color: 'var(--vf-text-3)' }}>{t('admin:twilioConfig.sipZadarma.sip.domainHint')}</p>
        </div>
        <Button type="submit" disabled={run.isPending} className="min-h-[44px]">
          {run.isPending ? t('admin:twilioConfig.sipZadarma.sip.running') : t('admin:twilioConfig.sipZadarma.sip.submit')}
        </Button>
      </div>
      {run.isError && <ErrorLine message={codeMessage(run.error, t('admin:twilioConfig.sipZadarma.sip.failed'))} error={run.error} />}
      {r && (
        <ResultList rows={[
          [t('admin:twilioConfig.sipZadarma.sip.resultDomain'), r.domainName],
          ['Domain SID', r.domainSid],
          ['IP ACL SID', r.aclSid],
          [t('admin:twilioConfig.sipZadarma.sip.resultState'), r.created ? t('admin:twilioConfig.sipZadarma.sip.stateCreated') : t('admin:twilioConfig.sipZadarma.sip.stateUpdated')],
        ]} />
      )}
    </form>
  );
}

function ZadarmaImportForm() {
  const { t } = useTranslation(['admin']);
  const codeMessage = useCodeMessage();
  const qc = useQueryClient();
  const orgs = useOrganisations();
  const [tenant, setTenant] = useState('');
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const run = useMutation<ZadarmaImportResult, unknown, { tenant_email: string; phone_number: string }>({
    mutationFn: (body) => importZadarmaNumber(body),
    onSuccess: (r) => {
      toast.success(r.imported ? t('admin:twilioConfig.sipZadarma.zadarma.imported') : t('admin:twilioConfig.sipZadarma.zadarma.updated'));
      void qc.invalidateQueries({ queryKey: ['twilio-all-numbers'] });
    },
  });
  const phoneValue = phone.replace(/[\s.-]/g, '');
  const phoneInvalid = touched && !E164.test(phoneValue);
  const tenantMissing = touched && !tenant;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!tenant || !E164.test(phoneValue)) return;
    run.mutate({ tenant_email: tenant, phone_number: phoneValue });
  };
  const r = run.data;
  // REV-W7-F14: a number goes to an organisation in service only (a disabled
  // or soft-deleted one would receive calls nobody can take).
  const organisations = (orgs.data?.organisations ?? []).filter((o) => (o.status ? o.status === 'active' : o.is_active));
  const numberStatus = (v?: string) => (v
    ? t(`admin:twilioConfig.numbers.statuses.${NUMBER_STATUSES.includes(v) ? v : 'other'}`, { status: v })
    : '—');
  const callerIdState = (v?: string) => t(`admin:twilioConfig.sipZadarma.zadarma.callerIdStates.${CALLER_ID_STATES.includes(v ?? '') ? (v || 'none') : 'other'}`, { state: v });
  return (
    <form onSubmit={submit} className="space-y-3" aria-labelledby="zadarma-import-title" noValidate>
      <h3 id="zadarma-import-title" className="text-sm font-semibold">{t('admin:twilioConfig.sipZadarma.zadarma.title')}</h3>
      <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.sipZadarma.zadarma.desc')}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-start">
        <div className="space-y-1">
          <Label htmlFor="zadarma-tenant">{t('admin:twilioConfig.sipZadarma.zadarma.organisation')}</Label>
          <Select value={tenant} onValueChange={setTenant}>
            <SelectTriggerWithId id="zadarma-tenant" className="min-h-[44px]">
              <SelectValue placeholder={orgs.isLoading ? t('admin:twilioConfig.sipZadarma.zadarma.loadingOrgs') : t('admin:twilioConfig.sipZadarma.zadarma.pickOrg')} />
            </SelectTriggerWithId>
            <SelectContent>
              {organisations.map((o) => <SelectItem key={o.id} value={o.email}>{o.company_name ? `${o.company_name} · ${o.email}` : o.email}</SelectItem>)}
            </SelectContent>
          </Select>
          {tenantMissing && <p className="text-xs" role="alert" style={{ color: 'var(--vf-fill-danger)' }}>{t('admin:twilioConfig.sipZadarma.zadarma.orgRequired')}</p>}
          {orgs.isError && <p className="text-xs" role="alert" style={{ color: 'var(--vf-fill-danger)' }}>{t('admin:twilioConfig.sipZadarma.zadarma.orgsFailed')}</p>}
        </div>
        <div className="space-y-1">
          <Label htmlFor="zadarma-phone">{t('admin:twilioConfig.sipZadarma.zadarma.phone')}</Label>
          <Input
            id="zadarma-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+33757690371"
            inputMode="tel"
            autoComplete="off"
            aria-invalid={phoneInvalid || undefined}
            aria-describedby="zadarma-phone-hint"
          />
          <p id="zadarma-phone-hint" className="text-xs" role={phoneInvalid ? 'alert' : undefined} style={{ color: phoneInvalid ? 'var(--vf-fill-danger)' : 'var(--vf-text-3)' }}>
            {phoneInvalid ? t('admin:twilioConfig.sipZadarma.zadarma.phoneInvalid') : t('admin:twilioConfig.sipZadarma.zadarma.phoneHint')}
          </p>
        </div>
        <Button type="submit" disabled={run.isPending} className="min-h-[44px] sm:mt-6">
          {run.isPending ? t('admin:twilioConfig.sipZadarma.zadarma.running') : t('admin:twilioConfig.sipZadarma.zadarma.submit')}
        </Button>
      </div>
      {run.isError && <ErrorLine message={codeMessage(run.error, t('admin:twilioConfig.sipZadarma.zadarma.failed'))} error={run.error} />}
      {r && (
        <ResultList rows={[
          [t('admin:twilioConfig.sipZadarma.zadarma.resultNumber'), r.number.phone_number],
          [t('admin:twilioConfig.sipZadarma.zadarma.resultAction'), r.imported ? t('admin:twilioConfig.sipZadarma.zadarma.actionCreated') : t('admin:twilioConfig.sipZadarma.zadarma.actionUpdated')],
          [t('admin:twilioConfig.sipZadarma.zadarma.resultStatus'), numberStatus(r.number.status)],
          ...(r.number.sip_uri ? [[t('admin:twilioConfig.sipZadarma.zadarma.resultSipUri'), r.number.sip_uri] as [string, ReactNode]] : []),
          [t('admin:twilioConfig.sipZadarma.zadarma.resultCallerId'), callerIdState(r.number.caller_id_verification)],
        ]} />
      )}
    </form>
  );
}

export function TwilioSipZadarmaPanel() {
  const { t } = useTranslation(['admin']);
  return (
    <section aria-labelledby="sip-zadarma-title" className="space-y-5 rounded-vf border p-4" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-bg)' }}>
      <div>
        <h2 id="sip-zadarma-title" className="text-base font-semibold">{t('admin:twilioConfig.sipZadarma.title')}</h2>
        <p className="text-sm" style={{ color: 'var(--vf-text-2)' }}>{t('admin:twilioConfig.sipZadarma.desc')}</p>
      </div>
      <SipDomainForm />
      <div className="border-t" style={{ borderColor: 'var(--vf-border)' }} />
      <ZadarmaImportForm />
    </section>
  );
}
